import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Search, Loader2, ExternalLink, X, Users, RefreshCw, UploadCloud, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAdminData } from "@/hooks/useAdminData";
import { SMARTLINK_PLATFORMS, SMARTLINK_SOCIALS } from "@/lib/smartlinkPlatforms";

const API = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_ORIGIN = window.location.origin;

const emptyForm = {
  upc: "", slug: "", title: "", artist: "", cover_url: "", release_date: "", status: "pre_release",
  platform_links: {}, social_links: {}, artist_note: "", preview_url: "",
};
const MIN_RECOMMENDED_PLATFORMS = 5;

export const AdminSmartlinksPanel = ({ onUnauthorized }) => {
  const { rows, loading, fetchError, fetchRows } = useAdminData({ onUnauthorized, endpoint: "/api/admin/smartlinks" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [looking, setLooking] = useState(false);
  const [lookupMsg, setLookupMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingSlug, setDeletingSlug] = useState(null);
  const [formError, setFormError] = useState("");
  const [leadsOpen, setLeadsOpen] = useState(false);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [leadsSlug, setLeadsSlug] = useState(null);
  const [refreshingSlug, setRefreshingSlug] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkUpcs, setBulkUpcs] = useState("");
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);

  const openCreate = () => {
    setForm(emptyForm);
    setEditingSlug(null);
    setLookupMsg("");
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      upc: row.upc || "", slug: row.slug, title: row.title, artist: row.artist,
      cover_url: row.cover_url, release_date: row.release_date || "", status: row.status || "pre_release",
      platform_links: row.platform_links || {}, social_links: row.social_links || {},
      artist_note: row.artist_note || "", preview_url: row.preview_url || "",
    });
    setEditingSlug(row.slug);
    setLookupMsg("");
    setFormError("");
    setDialogOpen(true);
  };

  const handleLookup = async () => {
    if (!form.upc.trim()) return;
    setLooking(true);
    setLookupMsg("");
    try {
      const res = await axios.get(`${API}/api/smartlinks/lookup`, {
        params: { upc: form.upc.trim() },
        withCredentials: true,
      });
      if (res.data.found) {
        setForm((f) => ({
          ...f,
          title: res.data.title || f.title,
          artist: res.data.artist || f.artist,
          cover_url: res.data.cover_url || f.cover_url,
          release_date: res.data.release_date || f.release_date,
          preview_url: res.data.preview_url || f.preview_url,
          platform_links: { ...res.data.platform_links, ...f.platform_links },
        }));
        setLookupMsg("Datos encontrados y cargados automáticamente.");
      } else {
        setLookupMsg("No se encontraron datos para ese UPC. Completa los campos manualmente.");
      }
    } catch {
      setLookupMsg("Error al consultar el UPC. Intenta de nuevo.");
    } finally {
      setLooking(false);
    }
  };

  const handlePlatformUrlChange = (key, value) => {
    setForm((f) => ({ ...f, platform_links: { ...f.platform_links, [key]: value } }));
  };

  const handleSocialUrlChange = (key, value) => {
    setForm((f) => ({ ...f, social_links: { ...f.social_links, [key]: value } }));
  };

  const handleSave = async () => {
    setFormError("");
    if (!form.title.trim() || !form.artist.trim() || !form.cover_url.trim() || (!editingSlug && !form.upc.trim())) {
      setFormError("Completa título, artista, portada y UPC.");
      return;
    }
    setSaving(true);
    try {
      const cleanedLinks = Object.fromEntries(
        Object.entries(form.platform_links).filter(([, v]) => v && v.trim())
      );
      const cleanedSocials = Object.fromEntries(
        Object.entries(form.social_links).filter(([, v]) => v && v.trim())
      );
      if (editingSlug) {
        await axios.put(`${API}/api/admin/smartlinks/${editingSlug}`, {
          title: form.title, artist: form.artist, cover_url: form.cover_url,
          release_date: form.release_date || null, status: form.status, platform_links: cleanedLinks,
          social_links: cleanedSocials, artist_note: form.artist_note.trim() || null, preview_url: form.preview_url.trim() || null,
        }, { withCredentials: true });
        toast.success("Smartlink actualizado");
      } else {
        await axios.post(`${API}/api/admin/smartlinks`, {
          upc: form.upc.trim(), slug: form.slug.trim() || null, title: form.title, artist: form.artist,
          cover_url: form.cover_url, release_date: form.release_date || null, status: form.status,
          platform_links: cleanedLinks, social_links: cleanedSocials,
          artist_note: form.artist_note.trim() || null, preview_url: form.preview_url.trim() || null,
        }, { withCredentials: true });
        toast.success("Smartlink creado");
      }
      setDialogOpen(false);
      fetchRows();
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized?.();
      else setFormError("No se pudo guardar el smartlink. Intenta de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slug) => {
    setDeletingSlug(slug);
    try {
      await axios.delete(`${API}/api/admin/smartlinks/${slug}`, { withCredentials: true });
      toast.success("Smartlink eliminado");
      fetchRows();
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized?.();
      else toast.error("No se pudo eliminar el smartlink.");
    } finally {
      setDeletingSlug(null);
    }
  };

  const handleCopy = async (slug) => {
    try {
      await navigator.clipboard.writeText(`${PUBLIC_ORIGIN}/smartlink/${slug}`);
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar el enlace");
    }
  };

  const handleViewLeads = async (slug) => {
    setLeadsSlug(slug);
    setLeadsOpen(true);
    setLeadsLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/smartlinks/${slug}/leads`, { withCredentials: true });
      setLeads(res.data);
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized?.();
      else toast.error("No se pudieron cargar los leads.");
    } finally {
      setLeadsLoading(false);
    }
  };

  const handleRefreshLinks = async (slug) => {
    setRefreshingSlug(slug);
    try {
      const res = await axios.post(`${API}/api/smartlinks/${slug}/refresh-links`);
      if (res.data.updated) {
        toast.success(`Nuevos enlaces encontrados: ${res.data.new_platforms.join(", ")}`);
        fetchRows();
      } else {
        toast("Sin novedades por ahora para este smartlink.");
      }
    } catch {
      toast.error("No se pudo actualizar los enlaces.");
    } finally {
      setRefreshingSlug(null);
    }
  };

  const openBulkImport = () => {
    setBulkUpcs("");
    setBulkResults(null);
    setBulkOpen(true);
  };

  const handleBulkImport = async () => {
    const upcs = bulkUpcs.split(/[\n,]+/).map((u) => u.trim()).filter(Boolean).slice(0, 20);
    if (upcs.length === 0) return;
    setBulkProcessing(true);
    setBulkResults(null);
    try {
      const res = await axios.post(`${API}/api/admin/smartlinks/bulk`, { upcs }, { withCredentials: true });
      setBulkResults(res.data.results);
      toast.success(`${res.data.created} de ${res.data.total} smartlinks creados`);
      fetchRows();
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized?.();
      else toast.error("No se pudo procesar la carga masiva.");
    } finally {
      setBulkProcessing(false);
    }
  };

  const bulkStatusLabel = {
    created: "Creado",
    skipped_duplicate: "Ya existía",
    skipped_no_data: "Sin datos suficientes",
    skipped_invalid: "UPC inválido",
    error: "Error",
  };

  return (
    <div data-testid="admin-smartlinks-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-neutral-500 text-sm">Crea páginas de pre-save/pre-add para tus próximos lanzamientos.</p>
        <div className="flex items-center gap-2">
          <Button data-testid="admin-smartlink-bulk-import-button" onClick={openBulkImport} variant="outline" className="border-white/15 gap-2">
            <UploadCloud className="w-4 h-4" /> Carga Masiva
          </Button>
          <Button data-testid="admin-smartlink-create-button" onClick={openCreate} className="bg-brand-red hover:bg-brand-red/90 text-white gap-2">
            <Plus className="w-4 h-4" /> Nuevo Smartlink
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-neutral-500 gap-2" data-testid="admin-smartlinks-loading">
          <Loader2 className="w-5 h-5 animate-spin" /> Cargando...
        </div>
      ) : fetchError ? (
        <p className="text-brand-red text-sm py-8 text-center" data-testid="admin-smartlinks-fetch-error">{fetchError}</p>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-neutral-500 text-sm" data-testid="admin-smartlinks-empty">
          Aún no has creado ningún smartlink.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((row) => (
            <div key={row.slug} data-testid={`admin-smartlink-card-${row.slug}`} className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
              <div className="aspect-square w-full bg-neutral-900">
                <img src={row.cover_url} alt={row.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <p className="font-semibold text-sm text-white truncate">{row.title}</p>
                <p className="text-neutral-500 text-xs truncate mb-2">{row.artist}</p>
                <span className={`inline-block text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 mb-3 ${
                  row.status === "released" ? "bg-brand-blue/15 text-brand-blue" : "bg-brand-red/15 text-brand-red"
                }`}>
                  {row.status === "released" ? "Disponible" : "Pre-lanzamiento"}
                </span>
                <div className="flex items-center gap-1.5">
                  <button data-testid={`admin-smartlink-copy-${row.slug}`} onClick={() => handleCopy(row.slug)} title="Copiar enlace" className="h-8 w-8 flex items-center justify-center rounded-lg border border-white/10 hover:bg-white/5 transition-colors">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <a data-testid={`admin-smartlink-open-${row.slug}`} href={`/smartlink/${row.slug}`} target="_blank" rel="noopener noreferrer" title="Abrir" className="h-8 w-8 flex items-center justify-center rounded-lg border border-white/10 hover:bg-white/5 transition-colors">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  <button data-testid={`admin-smartlink-leads-${row.slug}`} onClick={() => handleViewLeads(row.slug)} title="Ver leads" className="h-8 w-8 flex items-center justify-center rounded-lg border border-white/10 hover:bg-white/5 transition-colors">
                    <Users className="w-3.5 h-3.5" />
                  </button>
                  <button
                    data-testid={`admin-smartlink-refresh-${row.slug}`}
                    onClick={() => handleRefreshLinks(row.slug)}
                    disabled={refreshingSlug === row.slug}
                    title="Buscar enlaces nuevos (MusicBrainz/Spotify/Deezer)"
                    className="h-8 w-8 flex items-center justify-center rounded-lg border border-white/10 hover:bg-white/5 transition-colors disabled:opacity-50"
                  >
                    {refreshingSlug === row.slug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  </button>
                  <button data-testid={`admin-smartlink-edit-${row.slug}`} onClick={() => openEdit(row)} title="Editar" className="h-8 w-8 flex items-center justify-center rounded-lg border border-white/10 hover:bg-white/5 transition-colors">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    data-testid={`admin-smartlink-delete-${row.slug}`}
                    onClick={() => handleDelete(row.slug)}
                    disabled={deletingSlug === row.slug}
                    title="Eliminar"
                    className="h-8 w-8 flex items-center justify-center rounded-lg border border-white/10 hover:bg-brand-red/10 hover:border-brand-red/30 hover:text-brand-red transition-colors disabled:opacity-50"
                  >
                    {deletingSlug === row.slug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0F0F10] border-white/10 text-white max-w-lg" data-testid="admin-smartlink-dialog">
          <DialogHeader>
            <DialogTitle>{editingSlug ? "Editar Smartlink" : "Nuevo Smartlink"}</DialogTitle>
            <DialogDescription className="text-neutral-500">
              Busca por UPC para autocompletar los datos reales del lanzamiento.
            </DialogDescription>
          </DialogHeader>

          {!editingSlug && (
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="text-xs text-neutral-400 mb-1 block">Código UPC</label>
                <Input
                  data-testid="admin-smartlink-upc-input"
                  value={form.upc}
                  onChange={(e) => setForm((f) => ({ ...f, upc: e.target.value }))}
                  placeholder="024543757917"
                  className="bg-white/[0.03] border-white/10 text-white"
                />
              </div>
              <Button data-testid="admin-smartlink-lookup-button" onClick={handleLookup} disabled={looking} variant="outline" className="border-white/15 gap-2">
                {looking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Buscar
              </Button>
            </div>
          )}
          {lookupMsg && <p className="text-xs text-neutral-400" data-testid="admin-smartlink-lookup-message">{lookupMsg}</p>}

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs text-neutral-400 mb-1 block">Título</label>
              <Input data-testid="admin-smartlink-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="bg-white/[0.03] border-white/10 text-white" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-neutral-400 mb-1 block">Artista</label>
              <Input data-testid="admin-smartlink-artist-input" value={form.artist} onChange={(e) => setForm((f) => ({ ...f, artist: e.target.value }))} className="bg-white/[0.03] border-white/10 text-white" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-neutral-400 mb-1 block">URL de portada</label>
              <Input data-testid="admin-smartlink-cover-input" value={form.cover_url} onChange={(e) => setForm((f) => ({ ...f, cover_url: e.target.value }))} className="bg-white/[0.03] border-white/10 text-white" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-neutral-400 mb-1 block">URL de preview de audio (30s, opcional)</label>
              <Input data-testid="admin-smartlink-preview-input" value={form.preview_url} onChange={(e) => setForm((f) => ({ ...f, preview_url: e.target.value }))} placeholder="Se autocompleta con la búsqueda por UPC si está disponible" className="bg-white/[0.03] border-white/10 text-white" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-neutral-400 mb-1 block">Nota del artista (opcional)</label>
              <Textarea data-testid="admin-smartlink-note-input" value={form.artist_note} onChange={(e) => setForm((f) => ({ ...f, artist_note: e.target.value }))} placeholder="Un mensaje personal o la letra de la canción" rows={2} className="bg-white/[0.03] border-white/10 text-white" />
            </div>
            <div>
              <label className="text-xs text-neutral-400 mb-1 block">Fecha de lanzamiento</label>
              <Input type="date" data-testid="admin-smartlink-date-input" value={form.release_date} onChange={(e) => setForm((f) => ({ ...f, release_date: e.target.value }))} className="bg-white/[0.03] border-white/10 text-white" />
            </div>
            <div>
              <label className="text-xs text-neutral-400 mb-1 block">Estado</label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="admin-smartlink-status-select" className="bg-white/[0.03] border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pre_release">Pre-lanzamiento</SelectItem>
                  <SelectItem value="released">Disponible</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!editingSlug && (
              <div className="col-span-2">
                <label className="text-xs text-neutral-400 mb-1 block">Slug personalizado (opcional)</label>
                <Input data-testid="admin-smartlink-slug-input" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} placeholder="se genera automáticamente si se deja vacío" className="bg-white/[0.03] border-white/10 text-white" />
              </div>
            )}
          </div>

          <div>
            <p className="text-xs text-neutral-400 mb-2">Enlaces por plataforma (opcional, déjalos vacíos si aún no están disponibles)</p>
            {Object.values(form.platform_links).filter((v) => v && v.trim()).length < MIN_RECOMMENDED_PLATFORMS && (
              <div data-testid="admin-smartlink-min-platforms-warning" className="flex items-start gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/[0.08] p-2.5 mb-2">
                <AlertCircle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                <p className="text-amber-200 text-xs leading-relaxed">
                  Recomendamos al menos {MIN_RECOMMENDED_PLATFORMS} plataformas activas con enlace directo. Ahora tienes {Object.values(form.platform_links).filter((v) => v && v.trim()).length}.
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 gap-2 max-h-52 overflow-y-auto pr-1">
              {SMARTLINK_PLATFORMS.map((p) => (
                <div key={p.key} className="flex items-center gap-2">
                  <span className="text-xs text-neutral-400 w-28 shrink-0">{p.name}</span>
                  <Input
                    data-testid={`admin-smartlink-platform-url-${p.key}`}
                    value={form.platform_links[p.key] || ""}
                    onChange={(e) => handlePlatformUrlChange(p.key, e.target.value)}
                    placeholder="https://..."
                    className="bg-white/[0.03] border-white/10 text-white h-8 text-xs"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs text-neutral-400 mb-2">Redes sociales del artista (opcional, para el botón "Seguir")</p>
            <div className="grid grid-cols-1 gap-2">
              {SMARTLINK_SOCIALS.map((s) => (
                <div key={s.key} className="flex items-center gap-2">
                  <span className="text-xs text-neutral-400 w-28 shrink-0">{s.name}</span>
                  <Input
                    data-testid={`admin-smartlink-social-url-${s.key}`}
                    value={form.social_links[s.key] || ""}
                    onChange={(e) => handleSocialUrlChange(s.key, e.target.value)}
                    placeholder="https://..."
                    className="bg-white/[0.03] border-white/10 text-white h-8 text-xs"
                  />
                </div>
              ))}
            </div>
          </div>

          {formError && <p className="text-brand-red text-xs" data-testid="admin-smartlink-form-error">{formError}</p>}

          <DialogFooter>
            <Button data-testid="admin-smartlink-cancel-button" variant="outline" onClick={() => setDialogOpen(false)} className="border-white/15 gap-2">
              <X className="w-4 h-4" /> Cancelar
            </Button>
            <Button data-testid="admin-smartlink-save-button" onClick={handleSave} disabled={saving} className="bg-brand-red hover:bg-brand-red/90 text-white gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={leadsOpen} onOpenChange={setLeadsOpen}>
        <DialogContent className="bg-[#0F0F10] border-white/10 text-white max-w-md" data-testid="admin-smartlink-leads-dialog">
          <DialogHeader>
            <DialogTitle>Leads capturados</DialogTitle>
            <DialogDescription className="text-neutral-500">
              Fans interesados en {leadsSlug}
            </DialogDescription>
          </DialogHeader>
          {leadsLoading ? (
            <div className="flex items-center justify-center py-8 text-neutral-500 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Cargando...
            </div>
          ) : leads.length === 0 ? (
            <p className="text-neutral-500 text-sm py-8 text-center" data-testid="admin-smartlink-leads-empty">
              Aún no hay leads para este smartlink.
            </p>
          ) : (
            <div className="max-h-80 overflow-y-auto flex flex-col gap-2" data-testid="admin-smartlink-leads-list">
              {leads.map((lead, idx) => (
                <div key={idx} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                  <p className="text-sm font-semibold text-white">{lead.name}</p>
                  <p className="text-xs text-neutral-400">{lead.email}</p>
                  <p className="text-[10px] text-neutral-600 mt-1">{lead.created_at ? new Date(lead.created_at).toLocaleString("es-MX") : ""}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="bg-[#0F0F10] border-white/10 text-white max-w-lg" data-testid="admin-smartlink-bulk-dialog">
          <DialogHeader>
            <DialogTitle>Carga Masiva de UPCs</DialogTitle>
            <DialogDescription className="text-neutral-500">
              Pega hasta 20 códigos UPC (uno por línea). Buscamos automáticamente título, artista, portada y enlaces de plataformas (MusicBrainz + iTunes + Spotify + YouTube + Deezer, en ese orden) para crear un smartlink por cada uno. Recomendamos al menos {MIN_RECOMMENDED_PLATFORMS} plataformas activas por lanzamiento.
            </DialogDescription>
          </DialogHeader>

          <Textarea
            data-testid="admin-smartlink-bulk-textarea"
            value={bulkUpcs}
            onChange={(e) => setBulkUpcs(e.target.value)}
            placeholder={"024543757917\n886443919266\n..."}
            rows={6}
            className="bg-white/[0.03] border-white/10 text-white font-mono text-sm"
          />

          {bulkResults && (
            <div className="max-h-64 overflow-y-auto flex flex-col gap-1.5" data-testid="admin-smartlink-bulk-results">
              {bulkResults.map((r, idx) => (
                <div key={idx} className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-xs">
                  <span className="text-neutral-300 font-mono">{r.upc}</span>
                  <span className="text-neutral-500 truncate flex-1 text-right">{r.title ? `${r.title} — ${r.artist}` : r.reason || ""}</span>
                  {r.status === "created" && (
                    <span className={`shrink-0 text-[10px] font-bold ${r.platform_links_count < MIN_RECOMMENDED_PLATFORMS ? "text-amber-400" : "text-emerald-400"}`}>
                      {r.platform_links_count}/{MIN_RECOMMENDED_PLATFORMS}
                    </span>
                  )}
                  <span className={`shrink-0 font-semibold ${r.status === "created" ? "text-brand-blue" : r.status === "error" ? "text-brand-red" : "text-neutral-500"}`}>
                    {bulkStatusLabel[r.status] || r.status}
                  </span>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button data-testid="admin-smartlink-bulk-cancel-button" variant="outline" onClick={() => setBulkOpen(false)} className="border-white/15 gap-2">
              <X className="w-4 h-4" /> Cerrar
            </Button>
            <Button data-testid="admin-smartlink-bulk-submit-button" onClick={handleBulkImport} disabled={bulkProcessing || !bulkUpcs.trim()} className="bg-brand-red hover:bg-brand-red/90 text-white gap-2">
              {bulkProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
              {bulkProcessing ? "Procesando..." : "Procesar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
